// src/Pages/TestReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/TestReport.css';
import { auth, db } from '../firebaseConfig';
import {
  collection,
  getDocs,
  addDoc,
  query,
  orderBy,
  doc,
  getDoc
} from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaSpinner, FaPlus, FaTrashAlt } from 'react-icons/fa';

/* ------------------ Report Type Constants ------------------ */
const REPORT_TYPES = {
  AC_TEST: 'AC_TEST',
  FINAL_INSPECTION: 'FINAL_INSPECTION',
  CYLINDER: 'CYLINDER',
  DRIFTER: 'DRIFTER'
};
const REPORT_TYPE_LABELS = {
  [REPORT_TYPES.AC_TEST]: 'AC Test Report',
  [REPORT_TYPES.FINAL_INSPECTION]: 'Final Inspection Report',
  [REPORT_TYPES.CYLINDER]: 'Cylinder Test Report',
  [REPORT_TYPES.DRIFTER]: 'Drifter Test Report'
};

/* Cylinder + Drifter shared lists (unchanged) */
const cylinderLowList = [
  ['airRemoved','Air Removed'],
  ['lowVibration','No Vibration / Locking'],
  ['lowStaticLeak','No Static Seal Leak'],
  ['lowRodSealLeak','No Rod Seal Leak'],
  ['lowWeldLeak','No Weld Leak']
];
const cylinderHighList = [
  ['highVibration','No Vibration / Locking'],
  ['highRodSealLeak','No Rod Seal Leak'],
  ['highStaticSealLeak','No Static Seal Leak'],
  ['highWeldLeak','No Weld Leak'],
  ['fullExtHold','Hold @ Full Extension'],
  ['fullRetHold','Hold @ Full Retraction']
];
const drifterShiftNotesList = [
  'Check air & lubrication oil at shank adapter/front guide',
  'Inspect shank adapter threads',
  'Every 40 hours grease gear & tighten unions'
];

/* ================= Primitive UI Components ================= */
const Field = React.memo(function Field({ id, label, value, onChange, type='text', placeholder, className }) {
  return (
    <div className={`field ${className||''}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={value}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        autoComplete="off"
      />
    </div>
  );
});
const CheckItem = React.memo(function CheckItem({ id, label, checked, onChange, dataGroup }) {
  return (
    <label className="chk">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-group={dataGroup}
      />
      <span className="box" />
      <span className="lbl">{label}</span>
    </label>
  );
});
const CheckGrid = React.memo(function CheckGrid({ children }) {
  return <div className="check-grid-adv">{children}</div>;
});

/* Helper to build label/value object */
const pair = (label, value) => ({ label, value });

/* Small id helper */
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

/* ================= Main Component ================= */
export default function TestReport() {
  const navigate = useNavigate();

  const [reportType, setReportType] = useState(REPORT_TYPES.AC_TEST);
  const [reportId,   setReportId]   = useState('');
  const [jobs,       setJobs]       = useState([]);
  const [customers,  setCustomers]  = useState([]);
  const [saving,     setSaving]     = useState(false);

  const [header, setHeader] = useState({
    customer: '',
    jobId: '',
    status: 'Pending',
    date: new Date().toISOString().slice(0,10),
  });

  /* AC Test Report state */
  const [acTest, setAcTest] = useState({
    make:'', kw_hp:'', frameSize:'', poles:'', speed:'', unitNo:'',
    ratedVoltage:'', connection:'', rotorBalanced:false,
    ir1000:'', growler:'', condBurnt:false, condWet:false,
    condMech:false, condSingle:false, condOther:'',
    ambientTemp:'', coreTemp:'', coreWatt:'',
    loadVolt:'', phaseA:'', phaseB:'', phaseC:'',
    pressureVal:'', pressureDur:'', irPost:'',
    runVolt:'', lockedA:'', lockedB:'', lockedC:'', opVolt:'',
    bearingDE:'', bearingNDE:'', efficiency:'',
    vibDE:'', vibNDE:'', testedBy:''
  });

  /* Final Inspection - now fully dynamic checklist */
  const [finalInspection, setFinalInspection] = useState({
    description:'C FRAME PUMP',           // default still allowed
    items: [],                            // [{id, label, passed}]
    testedBy:''
  });
  const [newFiLabel, setNewFiLabel] = useState('');

  /* Cylinder Test */
  const [cylinder, setCylinder] = useState({
    serialNo:'', description:'OUTRIGGER CYLINDER', rodDia:'', barrelDia:'',
    openCentres:'', closedCentres:'',
    airRemoved:false, lowVibration:false, lowStaticLeak:false,
    lowRodSealLeak:false, lowWeldLeak:false,
    highVibration:false, highRodSealLeak:false, highStaticSealLeak:false,
    highWeldLeak:false, fullExtHold:false, fullRetHold:false,
    oilCondition:'', oilTemp:'', oilCleanliness:'', maxPressure:'',
    finalResult:'', testedBy:''
  });

  /* Drifter Test */
  const [drifter, setDrifter] = useState({
    drifterType:'RD314', serialNo:'', oilTempBefore:'', oilTempAfter:'',
    testingPressure:'', percussionTested:false, percussionTime:'',
    percussionRemarks:'', rotationTested:false, rotationTime:'',
    rotationRemarks:'', notesShift:true, checkedBy:''
  });

  /* ---------------- Load initial data ---------------- */
  useEffect(() => {
    const loadData = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const jSnap = await getDocs(query(collection(db, 'Jobs'), orderBy('createdAt', 'desc')));
        setJobs(jSnap.docs.map(d => d.data().jobId));

        const cSnap = await getDocs(collection(db, 'Customers'));
        setCustomers(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const trSnap = await getDocs(collection(db, 'TestReports'));
        setReportId(`TR${trSnap.size + 1}`);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load initial data');
      }
    };
    const unsub = auth.onAuthStateChanged(loadData);
    return () => unsub();
  }, []);

  /* ---------------- Handlers ---------------- */
  const changeHeader = useCallback(e => {
    const { id, value } = e.target;
    setHeader(prev => ({ ...prev, [id]: value }));
  }, []);

  const changeAC = useCallback(e => {
    const { id, value, type, checked } = e.target;
    setAcTest(prev => ({ ...prev, [id]: type==='checkbox'? checked : value }));
  }, []);

  /* Final inspection handlers */
  const changeFIField = useCallback(e => {
    const { id, value } = e.target;
    setFinalInspection(prev => ({ ...prev, [id]: value }));
  }, []);

  const toggleFiItem = useCallback((itemId) => {
    setFinalInspection(prev => ({
      ...prev,
      items: prev.items.map(i => i.id === itemId ? { ...i, passed: !i.passed } : i)
    }));
  }, []);

  const editFiItemLabel = useCallback((itemId, label) => {
    setFinalInspection(prev => ({
      ...prev,
      items: prev.items.map(i => i.id === itemId ? { ...i, label } : i)
    }));
  }, []);

  const removeFiItem = useCallback((itemId) => {
    setFinalInspection(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== itemId)
    }));
  }, []);

  const addFiItem = () => {
    const lbl = newFiLabel.trim();
    if (!lbl) return toast.error('Checklist item label is empty.');
    setFinalInspection(prev => ({
      ...prev,
      items: [...prev.items, { id: uid(), label: lbl, passed: false }]
    }));
    setNewFiLabel('');
  };

  /* Cylinder */
  const changeCylinder = useCallback(e => {
    const { id, value, type, checked } = e.target;
    setCylinder(prev => ({ ...prev, [id]: type==='checkbox'? checked : value }));
  }, []);

  /* Drifter */
  const changeDrifter = useCallback(e => {
    const { id, value, type, checked } = e.target;
    setDrifter(prev => ({ ...prev, [id]: type==='checkbox'? checked : value }));
  }, []);

  /* ---------------- Validation ---------------- */
  const validate = () => {
    if (!reportId)             return 'Report ID missing.';
    if (!header.customer)      return 'Customer is required.';
    if (!header.jobId)         return 'Job No. is required.';
    if (!header.date)          return 'Date is required.';
    switch (reportType) {
      case REPORT_TYPES.AC_TEST:
        if (!acTest.testedBy) return 'Tester required (AC Test).';
        break;
      case REPORT_TYPES.FINAL_INSPECTION:
        if (!finalInspection.testedBy) return 'Checked By required (Final Inspection).';
        break;
      case REPORT_TYPES.CYLINDER:
        if (!cylinder.serialNo) return 'Serial No required (Cylinder).';
        if (!cylinder.finalResult) return 'Final Result required (Cylinder).';
        break;
      case REPORT_TYPES.DRIFTER:
        if (!drifter.serialNo) return 'Serial No required (Drifter).';
        if (!drifter.checkedBy) return 'Checked By required (Drifter).';
        break;
      default: break;
    }
    return null;
  };

  /* ---------------- Build payload (no nested arrays) ---------------- */
  const buildPayloadForType = () => {
    switch (reportType) {
      case REPORT_TYPES.AC_TEST:
        return {
          acSections: [
            {
              section: 'Identification',
              data: [
                pair('Make', acTest.make),
                pair('kW/HP', acTest.kw_hp),
                pair('Frame Size', acTest.frameSize),
                pair('Poles', acTest.poles),
                pair('SpeedRPM', acTest.speed),
                pair('UnitNo', acTest.unitNo)
              ]
            },
            {
              section: 'Electrical',
              data: [
                pair('RatedVoltage', acTest.ratedVoltage),
                pair('Connection', acTest.connection),
                pair('RotorBalanced', acTest.rotorBalanced)
              ]
            },
            {
              section: 'PriorRewind',
              data: [
                pair('IR1000V', acTest.ir1000),
                pair('Growler', acTest.growler),
                pair('CondBurnt', acTest.condBurnt),
                pair('CondWet', acTest.condWet),
                pair('CondMech', acTest.condMech),
                pair('CondSingle', acTest.condSingle),
                pair('CondOther', acTest.condOther),
                pair('AmbientTemp', acTest.ambientTemp),
                pair('CoreTemp', acTest.coreTemp),
                pair('CoreWattLoss', acTest.coreWatt)
              ]
            },
            {
              section: 'AfterRewind',
              data: [
                pair('TestVoltage', acTest.loadVolt),
                pair('PhaseA', acTest.phaseA),
                pair('PhaseB', acTest.phaseB),
                pair('PhaseC', acTest.phaseC)
              ]
            },
            {
              section: 'AfterAssembly',
              data: [
                pair('PressureKV', acTest.pressureVal),
                pair('PressureDurationS', acTest.pressureDur),
                pair('IRPost', acTest.irPost),
                pair('RunVolt', acTest.runVolt),
                pair('LockedRotorA', acTest.lockedA),
                pair('LockedRotorB', acTest.lockedB),
                pair('LockedRotorC', acTest.lockedC),
                pair('OperatingVolt', acTest.opVolt)
              ]
            },
            {
              section: 'Performance',
              data: [
                pair('BearingDE', acTest.bearingDE),
                pair('BearingNDE', acTest.bearingNDE),
                pair('EfficiencyPct', acTest.efficiency),
                pair('VibrationDE', acTest.vibDE),
                pair('VibrationNDE', acTest.vibNDE)
              ]
            },
            {
              section: 'SignOff',
              data: [ pair('TestedBy', acTest.testedBy) ]
            }
          ]
        };

      case REPORT_TYPES.FINAL_INSPECTION:
        return {
          finalInspection: {
            description: finalInspection.description,
            finalInspectionItems: finalInspection.items.map(i => ({
              label: i.label,
              passed: !!i.passed
            })),
            signOff: [ pair('CheckedBy', finalInspection.testedBy) ]
          }
        };

      case REPORT_TYPES.CYLINDER:
        return {
          cylinder: {
            identity: [
              pair('SerialNo', cylinder.serialNo),
              pair('Description', cylinder.description),
              pair('RodDia', cylinder.rodDia),
              pair('BarrelDia', cylinder.barrelDia),
              pair('OpenCentres', cylinder.openCentres),
              pair('ClosedCentres', cylinder.closedCentres)
            ],
            cylinderChecksLow: cylinderLowList.map(([k,label]) => ({ label, ok: !!cylinder[k] })),
            cylinderChecksHigh: cylinderHighList.map(([k,label]) => ({ label, ok: !!cylinder[k] })),
            results: [
              pair('OilCondition', cylinder.oilCondition),
              pair('OilTemp', cylinder.oilTemp),
              pair('OilCleanliness', cylinder.oilCleanliness),
              pair('MaxPressure', cylinder.maxPressure),
              pair('FinalResult', cylinder.finalResult),
              pair('TestedBy', cylinder.testedBy)
            ]
          }
        };

      case REPORT_TYPES.DRIFTER:
        return {
          drifter: {
            idSection: [
              pair('DrifterType', drifter.drifterType),
              pair('SerialNo', drifter.serialNo),
              pair('TestingPressure', drifter.testingPressure)
            ],
            temperatures: [
              pair('OilBefore', drifter.oilTempBefore),
              pair('OilAfter', drifter.oilTempAfter)
            ],
            percussion: [
              { label: 'Percussion Tested', ok: drifter.percussionTested },
              pair('Percussion Time (min)', drifter.percussionTime),
              pair('Percussion Remarks', drifter.percussionRemarks)
            ],
            rotation: [
              { label: 'Rotation Tested', ok: drifter.rotationTested },
              pair('Rotation Time (min)', drifter.rotationTime),
              pair('Rotation Remarks', drifter.rotationRemarks)
            ],
            shiftNotes: drifter.notesShift ? drifterShiftNotesList : [],
            signOff: [ pair('CheckedBy', drifter.checkedBy) ]
          }
        };
      default:
        return {};
    }
  };

  /* ---------------- Save ---------------- */
  const saveReport = async e => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);

    const user = auth.currentUser;
    if (!user) return toast.error('Please log in.');

    try {
      setSaving(true);
      const snap = await getDoc(doc(db, 'users', user.uid));
      const { firstName = '', lastName = '' } = snap.exists() ? snap.data() : {};

      const payload = {
        reportId,
        reportType,
        header: { ...header },
        ...buildPayloadForType(),
        authId: user.uid,
        createdBy: `${firstName} ${lastName}`.trim(),
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'TestReports'), payload);

      toast.success('Report saved ✔');
      navigate('/Jobs');
    } catch (ex) {
      console.error(ex);
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Subform Blocks ---------------- */
  const AcTestForm = (
    <>
      <section className="card">
        <h3>Machine Identification</h3>
        <div className="grid-3">
          <Field id="make" label="Make" value={acTest.make} onChange={changeAC}/>
          <Field id="kw_hp" label="kW / HP" value={acTest.kw_hp} onChange={changeAC}/>
          <Field id="frameSize" label="Frame Size" value={acTest.frameSize} onChange={changeAC}/>
          <Field id="poles" label="Poles" value={acTest.poles} onChange={changeAC}/>
          <Field id="speed" label="Speed (RPM)" value={acTest.speed} onChange={changeAC}/>
          <Field id="unitNo" label="Unit No" value={acTest.unitNo} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>Electrical Configuration</h3>
        <div className="grid-3">
          <Field id="ratedVoltage" label="Rated Voltage" value={acTest.ratedVoltage} onChange={changeAC}/>
          <Field id="connection" label="Connection" value={acTest.connection} placeholder="Delta / Star" onChange={changeAC}/>
          <div className="field single-checkbox">
            <CheckItem id="rotorBalanced" label="Rotor Dynamically Balanced" checked={acTest.rotorBalanced} onChange={changeAC}/>
          </div>
        </div>
      </section>
      <section className="card">
        <h3>1. Prior to Rewind</h3>
        <div className="grid-2">
          <Field id="ir1000" label="IR @ 1000 V" value={acTest.ir1000} onChange={changeAC}/>
          <Field id="growler" label="Growler Test" value={acTest.growler} onChange={changeAC}/>
        </div>
        <div className="fieldset">
          <div className="legend">Condition Findings</div>
          <CheckGrid>
            <CheckItem id="condBurnt" label="Burnt-Out" checked={acTest.condBurnt} onChange={changeAC}/>
            <CheckItem id="condWet" label="Grease Wet" checked={acTest.condWet} onChange={changeAC}/>
            <CheckItem id="condMech" label="Mechanical Damage" checked={acTest.condMech} onChange={changeAC}/>
            <CheckItem id="condSingle" label="Single Phase" checked={acTest.condSingle} onChange={changeAC}/>
            <div className="field other-input">
              <input id="condOther" placeholder="Other issue…" value={acTest.condOther} onChange={changeAC}/>
            </div>
          </CheckGrid>
        </div>
        <h4>Core Test (after 15 min)</h4>
        <div className="grid-3">
          <Field id="ambientTemp" label="Ambient (°C)" value={acTest.ambientTemp} onChange={changeAC}/>
          <Field id="coreTemp" label="Core (°C)" value={acTest.coreTemp} onChange={changeAC}/>
          <Field id="coreWatt" label="Core Loss (W)" value={acTest.coreWatt} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>2. After Rewind</h3>
        <div className="grid-4">
          <Field id="loadVolt" label="Test Voltage (V)" value={acTest.loadVolt} onChange={changeAC}/>
          <Field id="phaseA" label="Phase A (A)" value={acTest.phaseA} onChange={changeAC}/>
          <Field id="phaseB" label="Phase B (A)" value={acTest.phaseB} onChange={changeAC}/>
          <Field id="phaseC" label="Phase C (A)" value={acTest.phaseC} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>3. After Assembly</h3>
        <div className="grid-3">
          <Field id="pressureVal" label="Pressure Test (kV)" value={acTest.pressureVal} onChange={changeAC}/>
          <Field id="pressureDur" label="Duration (s)" value={acTest.pressureDur} onChange={changeAC}/>
          <Field id="irPost" label="IR Post (MΩ)" value={acTest.irPost} onChange={changeAC}/>
        </div>
        <div className="grid-4">
          <Field id="runVolt" label="30 min Run V" value={acTest.runVolt} onChange={changeAC}/>
          <Field id="lockedA" label="Locked Rotor A" value={acTest.lockedA} onChange={changeAC}/>
          <Field id="lockedB" label="Locked Rotor B" value={acTest.lockedB} onChange={changeAC}/>
          <Field id="lockedC" label="Locked Rotor C" value={acTest.lockedC} onChange={changeAC}/>
        </div>
        <div className="grid-3">
          <Field id="opVolt" label="Operating Voltage (V)" value={acTest.opVolt} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>Performance</h3>
        <div className="grid-3">
          <Field id="bearingDE" label="Bearing DE" value={acTest.bearingDE} onChange={changeAC}/>
          <Field id="bearingNDE" label="Bearing NDE" value={acTest.bearingNDE} onChange={changeAC}/>
          <Field id="efficiency" label="Efficiency (%)" value={acTest.efficiency} onChange={changeAC}/>
          <Field id="vibDE" label="Vibration DE" value={acTest.vibDE} onChange={changeAC}/>
          <Field id="vibNDE" label="Vibration NDE" value={acTest.vibNDE} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>Sign-off</h3>
        <Field id="testedBy" label="Tested By" value={acTest.testedBy} onChange={changeAC}/>
      </section>
    </>
  );

  const FinalInspectionForm = (
    <>
      <section className="card">
        <h3>Inspection Checklist</h3>
        <Field id="description" label="Description" value={finalInspection.description} onChange={changeFIField}/>
        
        {/* Add item */}
        <div className="fi-add-row">
          <input
            className="fi-add-input"
            placeholder="Add checklist item…"
            value={newFiLabel}
            onChange={e => setNewFiLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFiItem(); } }}
          />
          <button type="button" className="fi-add-btn" onClick={addFiItem}>
            <FaPlus /> Add
          </button>
        </div>

        {/* Checklist items */}
        {finalInspection.items.length ? (
          <div className="fi-list">
            {finalInspection.items.map(it => (
              <div key={it.id} className="fi-item-row">
                <input
                  type="checkbox"
                  checked={it.passed}
                  onChange={() => toggleFiItem(it.id)}
                  aria-label={`Toggle ${it.label}`}
                />
                <input
                  className="fi-item-label"
                  value={it.label}
                  onChange={e => editFiItemLabel(it.id, e.target.value)}
                  placeholder="Item label…"
                />
                <button
                  type="button"
                  className="fi-del-btn"
                  onClick={() => removeFiItem(it.id)}
                  aria-label="Remove item"
                >
                  <FaTrashAlt />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted small" style={{marginTop:'0.6rem'}}>No checklist items yet. Add one above.</p>
        )}
      </section>

      <section className="card">
        <h3>Sign-off</h3>
        <Field id="testedBy" label="Checked By" value={finalInspection.testedBy} onChange={changeFIField}/>
      </section>
    </>
  );

  const CylinderForm = (
    <>
      <section className="card">
        <h3>Cylinder Identification</h3>
        <div className="grid-3">
          <Field id="serialNo" label="Serial No" value={cylinder.serialNo} onChange={changeCylinder}/>
          <Field id="description" label="Description" value={cylinder.description} onChange={changeCylinder}/>
          <Field id="rodDia" label="Rod Ø (mm)" value={cylinder.rodDia} onChange={changeCylinder}/>
          <Field id="barrelDia" label="Barrel Ø (mm)" value={cylinder.barrelDia} onChange={changeCylinder}/>
          <Field id="openCentres" label="Open Centres" value={cylinder.openCentres} onChange={changeCylinder}/>
          <Field id="closedCentres" label="Closed Centres" value={cylinder.closedCentres} onChange={changeCylinder}/>
        </div>
      </section>
      <section className="card">
        <h3>Low Pressure Test (&lt;10 bar)</h3>
        <CheckGrid>
          {cylinderLowList.map(([id,label])=>(
            <CheckItem key={id} id={id} label={label} checked={cylinder[id]} onChange={changeCylinder}/>
          ))}
        </CheckGrid>
      </section>
      <section className="card">
        <h3>High Pressure Test</h3>
        <CheckGrid>
          {cylinderHighList.map(([id,label])=>(
            <CheckItem key={id} id={id} label={label} checked={cylinder[id]} onChange={changeCylinder}/>
          ))}
        </CheckGrid>
        <div className="grid-4 mt1">
          <Field id="oilCondition" label="Oil Condition" value={cylinder.oilCondition} onChange={changeCylinder}/>
          <Field id="oilTemp" label="Oil Temp (°C)" value={cylinder.oilTemp} onChange={changeCylinder}/>
          <Field id="oilCleanliness" label="Oil Cleanliness" value={cylinder.oilCleanliness} onChange={changeCylinder}/>
          <Field id="maxPressure" label="Max Pressure (bar)" value={cylinder.maxPressure} onChange={changeCylinder}/>
        </div>
      </section>
      <section className="card">
        <h3>Result</h3>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="finalResult">Final Result</label>
            <select id="finalResult" value={cylinder.finalResult} onChange={changeCylinder}>
              <option value="">Select</option>
              <option value="PASSED">PASSED</option>
              <option value="FAILED">FAILED</option>
            </select>
          </div>
          <Field id="testedBy" label="Name & Signature" value={cylinder.testedBy} onChange={changeCylinder}/>
        </div>
      </section>
    </>
  );

  const DrifterForm = (
    <>
      <section className="card">
        <h3>Drifter Identification</h3>
        <div className="grid-3">
          <Field id="drifterType" label="Drifter Type" value={drifter.drifterType} onChange={changeDrifter}/>
          <Field id="serialNo" label="Serial No" value={drifter.serialNo} onChange={changeDrifter}/>
          <Field id="testingPressure" label="Testing Pressure (bar)" value={drifter.testingPressure} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Oil Temperature</h3>
        <div className="grid-2">
          <Field id="oilTempBefore" label="Before (°C)" value={drifter.oilTempBefore} onChange={changeDrifter}/>
          <Field id="oilTempAfter" label="After (°C)" value={drifter.oilTempAfter} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Percussion Mechanism</h3>
        <CheckGrid>
          <CheckItem id="percussionTested" label="Tested" checked={drifter.percussionTested} onChange={changeDrifter}/>
        </CheckGrid>
        <div className="grid-2">
          <Field id="percussionTime" label="Test Time (min)" value={drifter.percussionTime} onChange={changeDrifter}/>
          <Field id="percussionRemarks" label="Remarks" value={drifter.percussionRemarks} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Rotation Mechanism</h3>
        <CheckGrid>
          <CheckItem id="rotationTested" label="Tested" checked={drifter.rotationTested} onChange={changeDrifter}/>
        </CheckGrid>
        <div className="grid-2">
          <Field id="rotationTime" label="Test Time (min)" value={drifter.rotationTime} onChange={changeDrifter}/>
          <Field id="rotationRemarks" label="Remarks" value={drifter.rotationRemarks} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Shift Notes</h3>
        <CheckGrid>
          <CheckItem id="notesShift" label="Include Standard Shift Notes" checked={drifter.notesShift} onChange={changeDrifter}/>
        </CheckGrid>
        {drifter.notesShift && (
          <div className="shift-notes">
            <ul>{drifterShiftNotesList.map(n => <li key={n}>{n}</li>)}</ul>
          </div>
        )}
      </section>
      <section className="card">
        <h3>Sign-off</h3>
        <Field id="checkedBy" label="Checked By" value={drifter.checkedBy} onChange={changeDrifter}/>
      </section>
    </>
  );

  const renderBody = () => {
    switch (reportType) {
      case REPORT_TYPES.AC_TEST: return AcTestForm;
      case REPORT_TYPES.FINAL_INSPECTION: return FinalInspectionForm;
      case REPORT_TYPES.CYLINDER: return CylinderForm;
      case REPORT_TYPES.DRIFTER: return DrifterForm;
      default: return null;
    }
  };

  return (
    <div className="test-page">
      <ToastContainer position="top-right" autoClose={3000}/>
      <Sidebar/>
      <main className="test-main">
        <div className="title-row">
          <h2>{REPORT_TYPE_LABELS[reportType]}</h2>
          <span className="type-badge">{reportType}</span>
        </div>

        <div className="tabs">
          {Object.values(REPORT_TYPES).map(rt => (
            <button
              key={rt}
              type="button"
              className={`tab-btn ${reportType === rt ? 'active' : ''}`}
              onClick={() => setReportType(rt)}
              disabled={saving}
            >
              {REPORT_TYPE_LABELS[rt]}
            </button>
          ))}
        </div>

        <form className="test-form" onSubmit={saveReport} autoComplete="off">
          <section className="card header-card">
            <h3>Report Header</h3>
            <div className="grid-5">
              <div className="field">
                <label>Report ID</label>
                <input value={reportId} readOnly />
              </div>
              <div className="field">
                <label htmlFor="customer">Customer</label>
                <select id="customer" value={header.customer} onChange={changeHeader}>
                  <option value="">Select Customer</option>
                  {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="jobId">Job No.</label>
                <select id="jobId" value={header.jobId} onChange={changeHeader}>
                  <option value="">Select Job</option>
                  {jobs.map(j => <option key={j} value={j}>{j}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="status">Status</label>
                <select id="status" value={header.status} onChange={changeHeader}>
                  <option>Pending</option>
                  <option>In Progress</option>
                  <option>Completed</option>
                  <option>On Hold</option>
                  <option>Cancelled</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="date">Date</label>
                <input id="date" type="date" value={header.date} onChange={changeHeader}/>
              </div>
            </div>
          </section>

          {renderBody()}

          <div className="submit-wrap">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Report'}
            </button>
          </div>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={50}/>
          <p>Uploading…</p>
        </div>
      )}
    </div>
  );
}
